import { useMemo } from 'react';
import { ReadTemplate, useGetTemplatesForLocationQuery } from '../gql/graphql.generated';
import { useMyLocation } from '../useLocations';
import { TemplateOption } from '@/types/template.types';

const BUSYNESS_TARGET_NAME = 'Prediction';

interface TemplateProperties {
  color?: string;
  default?: boolean;
}

const getPropertiesFromTemplate = (template: ReadTemplate) => {
  if (!template.properties) {
    return {};
  }

  try {
    const properties = JSON.parse(template.properties);
    return {
      color: properties.color,
      default: properties.default
    }
  } catch (error) {
    console.log(error);
  }

  return {};
};

export const useTemplatesForLocation = () => {
  const selectedLocationId = useMyLocation().id;
  const [{ data }, refetch] = useGetTemplatesForLocationQuery({ variables: { locationId: selectedLocationId } });

  return useMemo(() => {
    const templatesForLocation = data?.templatesForLocation || [];

    const templatesToUse = templatesForLocation.filter(t => t.target?.type === BUSYNESS_TARGET_NAME);
    templatesToUse.sort((a, b) => a.target!.value - b.target!.value);

    if (templatesToUse.length === 0) {
      throw Error(`${BUSYNESS_TARGET_NAME} templates not set up for location ${selectedLocationId}`);
    }

    const templateOptions: TemplateOption[] = templatesToUse.map(template => ({
      name: template.name,
      id: template.id,
      ...getPropertiesFromTemplate(template as ReadTemplate)
    }));

    const customTemplates = templatesForLocation
      .filter(temp => {
        return temp.target?.type !== 'DayOfWeek';
      })
      .sort((a, b) => {
        if (a.name === b.name) {
          return 0;
        }

        return a.name > b.name ? 1 : -1;
      });

    const customTemplateOptions = customTemplates.map(template => {
      const option: TemplateOption = {
        name: template.name,
        id: template.id,
      ...getPropertiesFromTemplate(template as ReadTemplate)
      };

      return option;
    });

    const allTemplateOptions = [...templateOptions, ...customTemplateOptions];

    return { templateOptions, customTemplateOptions, allTemplateOptions, refetch };
  }, [data, refetch]);
};
